/**
 * KillSwitchBanner — pause/resume pushes to the tracker.
 * Used inside the Flow Optimizer toolbar. While the tracker's own switch
 * holds its optimizer (Tres Commas), the status reads "Held", never
 * "Pushes active"; Voluum and Binom never pass a reason (markup unchanged).
 */

import { FC } from 'react';
import { HelpCircle } from 'lucide-react';
import { trackerLabel, type TrackerId } from '@trafficpulse/shared';

import { Tooltip } from '@/components/ui/Tooltip';
import PushStatusLabel from '@/features/flow-optimizer/components/kill-switch/PushStatusLabel';
import PushToggleButton from '@/features/flow-optimizer/components/kill-switch/PushToggleButton';

export interface KillSwitchBannerProps {
  killSwitch: boolean;
  onToggle: (next: boolean) => void;
  disabled?: boolean;
  provider?: TrackerId;
  /** Why the tracker's optimizer is held (its full hold state), or null. */
  heldReason?: string | null;
}

function buildPauseHelp(tracker: string): string {
  return (
    `Pauses pushing optimized weights to ${tracker}. ` +
    'Preview, rules, and formulas still work. ' +
    'Scheduled auto-runs are skipped while paused. ' +
    'This is an emergency brake — not the same as turning off TrafficPulse Optimisation mode.'
  );
}

const KillSwitchBanner: FC<KillSwitchBannerProps> = ({
  killSwitch,
  onToggle,
  disabled,
  provider = 'voluum',
  heldReason = null,
}) => {
  const tracker = trackerLabel(provider);
  return (
    <div className="flex items-center gap-1.5">
      <PushStatusLabel paused={killSwitch} tracker={tracker} heldReason={heldReason} />
      <PushToggleButton paused={killSwitch} tracker={tracker} onToggle={onToggle} disabled={disabled} />
      <Tooltip content={buildPauseHelp(tracker)} side="bottom" interactive contentClassName="max-w-sm">
        <HelpCircle
          className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
          aria-label="What pause pushes does"
        />
      </Tooltip>
    </div>
  );
};

export default KillSwitchBanner;
